import Bugsnag from '@bugsnag/browser';

export const AXIS_URL_PREFIX = '/axis';
export const AXIS_DEMO_URL_PREFIX = `${AXIS_URL_PREFIX}/demo`;

export const makeAxisUrl = (
  slug: string,
  options?: { demo?: boolean },
): string => {
  const base = options?.demo ? AXIS_DEMO_URL_PREFIX : AXIS_URL_PREFIX;
  return `${base}/${slug}`;
};

export const WELCOME_PAGE_SLUG = 'welcome';
export const WELCOME_PAGE_URL = makeAxisUrl(WELCOME_PAGE_SLUG);

export const OTHER_INQUIRY_PAGE_SLUG = 'otherInquiry';
export const OTHER_INQUIRY_PAGE_URL = makeAxisUrl(OTHER_INQUIRY_PAGE_SLUG);

export const PROMOTIONAL_PAGE_SLUG = 'promotional';
export const PROMOTIONAL_PAGE_URL = makeAxisUrl(PROMOTIONAL_PAGE_SLUG);

export const PAYMENTS_PAGE_SLUG = 'payments';
export const PAYMENTS_PAGE_URL = makeAxisUrl(PAYMENTS_PAGE_SLUG);

export const CLAIMS_INQUIRY_PAGE_SLUG = 'claimsInquiry';
export const CLAIMS_INQUIRY_PAGE_URL = makeAxisUrl(CLAIMS_INQUIRY_PAGE_SLUG);

export const CURRENT_CLIENT_PAGE_SLUG = 'currentClient';
export const CURRENT_CLIENT_PAGE_URL = makeAxisUrl(CURRENT_CLIENT_PAGE_SLUG);

export const NEW_CLIENT_PAGE_SLUG = 'newClient';
export const NEW_CLIENT_PAGE_URL = makeAxisUrl(NEW_CLIENT_PAGE_SLUG);

export const THANKS_PAGE_SLUG = 'thanks';
export const THANKS_PAGE_URL = makeAxisUrl(THANKS_PAGE_SLUG);

export const DEMO_TEST_SLUG = 'test';
export const DEMO_ROUTE_SLUG = `demo/${DEMO_TEST_SLUG}`;
export const DEMO_TEST_URL = makeAxisUrl(DEMO_TEST_SLUG, { demo: true });

const AXIS_PAGES = [
  'welcome',
  'otherInquiry',
  'promotional',
  'payments',
  'claimsInquiry',
  'currentClient',
  'newClient',
  'thanks',
  'demoTest',
] as const;
export type AxisPage = typeof AXIS_PAGES[number];

export const axisPaths: Record<AxisPage, { slug: string; url: string }> = {
  welcome: { slug: WELCOME_PAGE_SLUG, url: WELCOME_PAGE_URL },
  otherInquiry: { slug: OTHER_INQUIRY_PAGE_SLUG, url: OTHER_INQUIRY_PAGE_URL },
  promotional: { slug: PROMOTIONAL_PAGE_SLUG, url: PROMOTIONAL_PAGE_URL },
  payments: { slug: PAYMENTS_PAGE_SLUG, url: PAYMENTS_PAGE_URL },
  claimsInquiry: {
    slug: CLAIMS_INQUIRY_PAGE_SLUG,
    url: CLAIMS_INQUIRY_PAGE_URL,
  },
  currentClient: {
    slug: CURRENT_CLIENT_PAGE_SLUG,
    url: CURRENT_CLIENT_PAGE_URL,
  },
  newClient: { slug: NEW_CLIENT_PAGE_SLUG, url: NEW_CLIENT_PAGE_URL },
  thanks: { slug: THANKS_PAGE_SLUG, url: THANKS_PAGE_URL },
  demoTest: { slug: DEMO_ROUTE_SLUG, url: DEMO_TEST_URL },
};

const AXIS_PAGE_TITLES: Record<string, string> = {
  [AXIS_URL_PREFIX]: 'Axis Insurance | Welcome',
  [axisPaths.welcome.url]: 'Axis Insurance | Welcome',
  [axisPaths.newClient.url]: 'Axis Insurance | New Client',
  [axisPaths.currentClient.url]: 'Axis Insurance | Current or Former Client',
  [axisPaths.claimsInquiry.url]: 'Axis Insurance | Claims Inquiry',
  [axisPaths.payments.url]: 'Axis Insurance | Finance or Payment',
  [axisPaths.promotional.url]: 'Axis Insurance | Marketing or Promotional',
  [axisPaths.otherInquiry.url]: 'Axis Insurance | Other Inquiry',
  [axisPaths.thanks.url]: 'Axis Insurance | Thank You',
  [axisPaths.demoTest.url]: 'Axis Insurance | Test',
};

const DEFAULT_AXIS_PAGE_TITLE = 'Axis Insurance';

const stripTrailingSlash = (pathname: string): string =>
  pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;

export function getAxisPageTitle(pathname: string): string {
  const pathWithoutTrailingSlash = stripTrailingSlash(pathname);

  if (
    Object.prototype.hasOwnProperty.call(
      AXIS_PAGE_TITLES,
      pathWithoutTrailingSlash,
    )
  ) {
    return AXIS_PAGE_TITLES[pathWithoutTrailingSlash];
  }

  Bugsnag.notify(
    new Error(`Missing Axis page title for pathname: ${pathname}`),
  );

  return DEFAULT_AXIS_PAGE_TITLE;
}
